/**
 * Secure Storage Utility for DowUrk Platform
 * 
 * SECURITY UPDATE: Tokens are now stored in httpOnly cookies by the backend.
 * This module provides backward compatibility and handles the transition.
 * 
 * httpOnly cookies are:
 * - Not accessible via JavaScript (protects against XSS)
 * - Automatically sent with requests (no manual header management needed)
 * - More secure than localStorage/sessionStorage
 */

const STORAGE_KEYS = {
  AUTH_TOKEN: 'token',           // Legacy key - will be phased out
  USER_DATA: 'user',             // User profile data (non-sensitive)
  SESSION_ID: 'session_id'       // For analytics/tracking
};

// Simple obfuscation for backward compatibility with existing stored tokens
const obfuscate = (str) => {
  if (!str) return '';
  return btoa(str.split('').reverse().join(''));
};

const deobfuscate = (str) => {
  if (!str) return '';
  try {
    return atob(str).split('').reverse().join('');
  } catch {
    return str; // Return original if decode fails (backwards compatibility)
  }
};

/**
 * Check if we're in a browser environment
 */
const isBrowser = typeof window !== 'undefined';

/**
 * Get auth token - checks localStorage for backward compatibility
 * Note: With httpOnly cookies, the token is sent automatically by the browser
 * This function is kept for backward compatibility during transition
 */
export const getAuthToken = () => {
  if (!isBrowser) return null;
  
  // Check for obfuscated token first
  const obfuscatedToken = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
  if (obfuscatedToken) {
    const deobfuscated = deobfuscate(obfuscatedToken);
    // If deobfuscation changed the value, it was obfuscated
    if (deobfuscated !== obfuscatedToken) {
      return deobfuscated;
    }
    // Otherwise return as-is (plain token)
    return obfuscatedToken;
  }
  
  return null;
};

/**
 * Set auth token in localStorage
 * Note: The backend also sets an httpOnly cookie, so this is redundant
 * but kept for backward compatibility during the transition period
 */
export const setAuthToken = (token) => {
  if (!isBrowser || !token) return false;
  
  try {
    // Store obfuscated for slight protection against casual inspection
    localStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, obfuscate(token));
    return true;
  } catch (error) {
    console.error('Error storing token:', error);
    return false;
  }
};

/**
 * Remove auth token from localStorage
 * Note: To fully logout, also call /api/auth/logout to clear the httpOnly cookie
 */
export const removeAuthToken = () => {
  if (!isBrowser) return false;
  try {
    localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
    return true;
  } catch (error) {
    console.error('Error removing token:', error);
    return false;
  }
};

/**
 * Get user data from storage
 */
export const getUserData = () => {
  if (!isBrowser) return null;
  
  try {
    const userData = localStorage.getItem(STORAGE_KEYS.USER_DATA);
    return userData ? JSON.parse(userData) : null;
  } catch (error) {
    console.error('Error parsing user data:', error);
    return null;
  }
};

/**
 * Set user data in storage
 */
export const setUserData = (user) => {
  if (!isBrowser || !user) return false;
  
  try {
    // Don't store sensitive data
    const safeUserData = {
      id: user.id,
      email: user.email,
      full_name: user.full_name,
      business_name: user.business_name,
      professional_role: user.professional_role,
      membership_tier: user.membership_tier,
      user_type: user.user_type
    };
    localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(safeUserData));
    return true;
  } catch (error) {
    console.error('Error storing user data:', error);
    return false;
  }
};

/**
 * Remove user data from storage
 */
export const removeUserData = () => {
  if (!isBrowser) return false;
  try {
    localStorage.removeItem(STORAGE_KEYS.USER_DATA);
    return true;
  } catch (error) {
    console.error('Error removing user data:', error);
    return false;
  }
};

/**
 * Clear all auth-related storage AND call logout endpoint to clear httpOnly cookie
 */
export const clearAllStorage = async () => {
  if (!isBrowser) return;
  
  // Clear localStorage
  localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
  localStorage.removeItem(STORAGE_KEYS.USER_DATA);
  
  // Call logout endpoint to clear httpOnly cookie
  try {
    const API = process.env.REACT_APP_BACKEND_URL;
    await fetch(`${API}/api/auth/logout`, {
      method: 'POST',
      credentials: 'include' // Important: include cookies in the request
    });
  } catch (error) {
    console.error('Error calling logout endpoint:', error);
  }
};

/**
 * Clear sensitive data (alias for clearAllStorage)
 */
export const clearSensitiveData = () => {
  removeAuthToken();
  removeUserData();
};

/**
 * Check if user is authenticated
 * With httpOnly cookies, we can't directly check the token
 * But we check localStorage for backward compatibility
 */
export const isAuthenticated = () => {
  if (!isBrowser) return false;
  return !!getAuthToken();
};

/**
 * Get session ID (for analytics)
 */
export const getSessionId = () => {
  if (!isBrowser) return null;
  
  let sessionId = sessionStorage.getItem(STORAGE_KEYS.SESSION_ID);
  if (!sessionId) {
    sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem(STORAGE_KEYS.SESSION_ID, sessionId);
  }
  return sessionId;
};

// Backward compatible exports
export const secureSet = (key, value) => {
  if (!isBrowser) return false;
  try {
    const stringValue = typeof value === 'string' ? value : JSON.stringify(value);
    localStorage.setItem(key, stringValue);
    return true;
  } catch (error) {
    console.error('SecureStorage: Error setting item', error);
    return false;
  }
};

export const secureGet = (key) => {
  if (!isBrowser) return null;
  try {
    return localStorage.getItem(key);
  } catch (error) {
    console.error('SecureStorage: Error getting item', error);
    return null;
  }
};

export const secureRemove = (key) => {
  if (!isBrowser) return false;
  try {
    localStorage.removeItem(key);
    return true;
  } catch (error) {
    console.error('SecureStorage: Error removing item', error);
    return false;
  }
};

export default {
  set: secureSet,
  get: secureGet,
  remove: secureRemove,
  getAuthToken,
  setAuthToken,
  removeAuthToken,
  getUserData,
  setUserData,
  removeUserData,
  clearAllStorage,
  clearSensitiveData,
  isAuthenticated,
  getSessionId,
  STORAGE_KEYS
};
